import { useState } from 'react';

import { useKnownAvailableParams, useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';

import type { Endorsement } from '../../../api';
import { useDeleteEndorsement, useEndorsements } from '../../../hooks';

import { useEndorsementHandlers } from './useEndorsementHandlers';

type EndorsementSectionReturn = {
	endorsements: readonly Endorsement[];
	isContentExpanded: boolean;
	isDeleteModalOpen: boolean;
	isDeleting: boolean;
	isHelpModalOpen: boolean;
	setIsDeleteModalOpen: (open: boolean) => void;
	setIsHelpModalOpen: (open: boolean) => void;
	handleAddAnother: () => void;
	handleViewOnPetition: () => void;
	handleEdit: (id: string) => void;
	handleRemove: (id: string) => void;
	handleSeeExampleClick: () => void;
	handleConfirmDelete: () => Promise<void>;
	handleToggleContentExpansion: () => void;
};

export function useEndorsementSection(): EndorsementSectionReturn {
	const { slug } = useKnownAvailableParams<{ slug: string }>();
	const navigate = useNavigate();
	const track = useTracking();
	const [isContentExpanded, setIsContentExpanded] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
	const [endorsementToDelete, setEndorsementToDelete] = useState<string | null>(null);

	const {
		data: { petitionId, endorsements },
		actions: { refetch },
	} = useEndorsements({ petitionSlugOrId: slug });

	const {
		data: { isDeleting },
		actions: { deleteEndorsement: deleteEndorsementAction },
	} = useDeleteEndorsement({
		petitionId,
		onSuccess: async () => {
			setIsDeleteModalOpen(false);
			setEndorsementToDelete(null);
			await refetch();
		},
	});

	const handlers = useEndorsementHandlers({
		slug,
		navigate,
		track,
		endorsementToDelete,
		setEndorsementToDelete,
		setIsDeleteModalOpen,
		setIsHelpModalOpen,
		setIsContentExpanded,
		deleteEndorsementAction,
	});

	return {
		endorsements,
		isContentExpanded,
		isDeleteModalOpen,
		isDeleting,
		isHelpModalOpen,
		setIsDeleteModalOpen,
		setIsHelpModalOpen,
		...handlers,
	};
}
